import type { RequirementsDocumentOutput } from '@/lib/interview/types';

interface RequirementsTabProps {
  requirements: RequirementsDocumentOutput;
}

export function RequirementsTab({ requirements }: RequirementsTabProps) {
  return (
    <div className="space-y-6">
      {/* Company Profile */}
      <div className="bg-white rounded-2xl p-6 border border-off-black/10">
        <h2 className="font-display font-light text-2xl text-off-black mb-4">Bedrijfsprofiel</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-off-black/50 text-sm">Branche</p>
            <p className="text-off-black">{requirements.company_profile.industry}</p>
          </div>
          <div>
            <p className="text-off-black/50 text-sm">Grootte</p>
            <p className="text-off-black">{requirements.company_profile.size}</p>
          </div>
          <div className="col-span-2">
            <p className="text-off-black/50 text-sm">Cultuur</p>
            <p className="text-off-black">{requirements.company_profile.culture}</p>
          </div>
        </div>
      </div>

      {/* Current State */}
      <div className="bg-white rounded-2xl p-6 border border-off-black/10">
        <h2 className="font-display font-light text-2xl text-off-black mb-4">Huidige situatie</h2>
        {requirements.current_state.tech_stack.length > 0 && (
          <div className="mb-4">
            <p className="text-off-black/50 text-sm mb-2">Technologiestack</p>
            <div className="flex flex-wrap gap-2">
              {requirements.current_state.tech_stack.map((tech, i) => (
                <span key={i} className="px-3 py-1 bg-off-white rounded-md text-sm text-off-black/70 border border-off-black/10">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        )}
        {requirements.current_state.pain_points.length > 0 && (
          <div>
            <p className="text-off-black/50 text-sm mb-2">Knelpunten</p>
            <ul className="space-y-1">
              {requirements.current_state.pain_points.map((pain, i) => (
                <li key={i} className="text-off-black/70 flex items-start gap-2">
                  <span className="text-orange-cta">&#8226;</span>
                  {pain}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Desired Outcomes */}
      <div className="bg-white rounded-2xl p-6 border border-off-black/10">
        <h2 className="font-display font-light text-2xl text-off-black mb-4">Gewenste resultaten</h2>
        <p className="text-off-black/70 mb-4">{requirements.desired_outcomes.vision}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-off-black/50 text-sm mb-2">Succescriteria</p>
            <ul className="space-y-1">
              {requirements.desired_outcomes.success_metrics.map((metric, i) => (
                <li key={i} className="text-off-black/70 text-sm flex items-start gap-2">
                  <span className="text-off-black/50">&#10003;</span>
                  {metric}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-off-black/50 text-sm mb-2">Tijdlijn</p>
            <p className="text-off-black/70">{requirements.desired_outcomes.timeline}</p>
          </div>
        </div>
      </div>

      {/* Stakeholders */}
      {requirements.stakeholders.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <h2 className="font-display font-light text-2xl text-off-black mb-4">Betrokkenen</h2>
          <div className="space-y-3">
            {requirements.stakeholders.map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <div>
                  <p className="text-off-black">{s.name || s.role}</p>
                  {s.name && <p className="text-off-black/50 text-sm">{s.role}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
